'use client';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { PREVIEW_CROSSFADE_CLASS_NAME, PREVIEW_CROSSFADE_DURATION_MS } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/constants';
import type { PreviewEdgeBackdropLayer, SpartanProductCustomizePreviewEdgeBackdropHandle, SpartanProductCustomizePreviewEdgeBackdropProps } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/edge-backdrop/types';
import type { SpartanCustomizePreviewTone } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/preview-tone';
import styles from '@/app/lcv/components/sections/spartan/products/customize/styles.module.css';
import { SPARTAN_PRODUCT_CUSTOMIZE_TRACKING } from '@/app/lcv/components/sections/spartan/products/customize/tracking';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
export const SpartanProductCustomizePreviewEdgeBackdrop = forwardRef<SpartanProductCustomizePreviewEdgeBackdropHandle, SpartanProductCustomizePreviewEdgeBackdropProps>(function SpartanProductCustomizePreviewEdgeBackdrop({
  tone,
  holdForImage = false
}, ref) {
  const isMountedRef = useRef(true);
  const transitionFrameRef = useRef<number | null>(null);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeTone, setActiveTone] = useState<SpartanCustomizePreviewTone>(tone);
  const [incomingTone, setIncomingTone] = useState<SpartanCustomizePreviewTone | null>(null);
  const [isIncomingVisible, setIsIncomingVisible] = useState<boolean>(false);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)') ?? false;
  const clearTransition = useCallback(() => {
    if (transitionFrameRef.current !== null) {
      cancelAnimationFrame(transitionFrameRef.current);
      transitionFrameRef.current = null;
    }
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
  }, []);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      clearTransition();
      isMountedRef.current = false;
    };
  }, [clearTransition]);
  const commitTone = useCallback((nextTone: SpartanCustomizePreviewTone) => {
    setActiveTone(nextTone);
    setIncomingTone(null);
    setIsIncomingVisible(false);
  }, []);
  const startFade = useCallback((nextTone_0: SpartanCustomizePreviewTone) => {
    if (nextTone_0 === incomingTone) return;
    clearTransition();
    if (nextTone_0 === activeTone) {
      setIncomingTone(null);
      setIsIncomingVisible(false);
      return;
    }
    if (prefersReducedMotion) {
      commitTone(nextTone_0);
      return;
    }
    setIncomingTone(nextTone_0);
    setIsIncomingVisible(false);
  }, [activeTone, clearTransition, commitTone, incomingTone, prefersReducedMotion]);
  const startFadeRef = useRef(startFade);
  startFadeRef.current = startFade;
  useImperativeHandle(ref, () => ({
    fadeTo: nextTone_1 => {
      startFadeRef.current(nextTone_1);
    },
    setTo: nextTone_2 => {
      clearTransition();
      commitTone(nextTone_2);
    }
  }));
  useEffect(() => {
    if (holdForImage) return;
    startFadeRef.current(tone);
  }, [holdForImage, tone]);
  useEffect(() => {
    if (incomingTone === null) return;
    transitionFrameRef.current = requestAnimationFrame(() => {
      transitionFrameRef.current = requestAnimationFrame(() => {
        transitionFrameRef.current = null;
        if (!isMountedRef.current) return;
        setIsIncomingVisible(true);
        transitionTimeoutRef.current = setTimeout(() => {
          if (!isMountedRef.current) return;
          commitTone(incomingTone);
          transitionTimeoutRef.current = null;
        }, PREVIEW_CROSSFADE_DURATION_MS);
      });
    });
    return clearTransition;
  }, [clearTransition, commitTone, incomingTone]);
  const layers: PreviewEdgeBackdropLayer[] = [{
    tone: activeTone,
    isActive: true,
    isIncoming: false
  }, ...(incomingTone ? [{
    tone: incomingTone,
    isActive: false,
    isIncoming: true
  }] : [])];
  return <div aria-hidden="true" className="pointer-events-none absolute inset-0" {...createDataAttributes({
    test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.previewEdge
  })}>
      {layers.map(layer => <div key={`${layer.isIncoming ? 'incoming' : 'active'}:${layer.tone}`} className={cn(styles.previewOuter, 'absolute inset-0', layer.isIncoming && PREVIEW_CROSSFADE_CLASS_NAME, layer.isActive && 'opacity-100', {
      'opacity-100': layer.isIncoming && isIncomingVisible,
      'opacity-0': layer.isIncoming && !isIncomingVisible
    })} data-preview-tone={layer.tone} />)}
    </div>;
});